import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { debounceFunction } from "../../utils/debounce";
import { Input } from "./Input";
import { MultiSelectOptionType } from "./MultiSelect";
import { CheckIcon } from "./icons/CheckIcon";

type Props = {
  isOpen: boolean;
  uuid: string;
  options: MultiSelectOptionType[];
  width: string;
  selectedValues: any[];
  handleSelectOption: (option: MultiSelectOptionType) => void;
  handleClearAll: () => void;
  hasSearchInput?: boolean;
  clearable?: boolean;
};

export const MultiSelectDropdown: React.FC<Props> = ({
  isOpen,
  uuid,
  options,
  width,
  selectedValues,
  handleSelectOption,
  handleClearAll,
  hasSearchInput = true,
  clearable = true,
}) => {
  const [optionsToShow, setOptionsToShow] =
    useState<MultiSelectOptionType[]>(options);

  const handleOnSearch = useMemo(() => {
    return debounceFunction((value: string) => {
      const filteredOptions = options.filter((option) =>
        option.name.toLowerCase().includes(value.toLowerCase()),
      );

      setOptionsToShow(filteredOptions);
    }, 500);
  }, [options]);

  useEffect(() => {
    setOptionsToShow(options);
  }, [options]);

  return (
    <StyledDropdownContainer $width={width} $isOpen={isOpen}>
      {hasSearchInput && (
        <StyledDropdownSearch id={`multiselect-search-${uuid}`}>
          <Input
            uuid={uuid}
            name={`search-${uuid}`}
            placeholder="Search ..."
            onChange={handleOnSearch}
            clearable
          />
        </StyledDropdownSearch>
      )}

      <StyledOptionContainer id={`multiselect-options-${uuid}`}>
        {optionsToShow.length === 0 && (
          <StyledNoOptionMessage>No Result</StyledNoOptionMessage>
        )}

        {optionsToShow.map((option, i) => {
          const isSelected = selectedValues.some(value =>
            value === option.value || String(value) === String(option.value)
          );
          return (
            <StyledDropdownOption
              key={`${option.id}-${i}`}
              selected={isSelected}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectOption(option);
              }}
            >
              <CheckIcon
                fillColor={
                  isSelected ? "#000000" : "transparent"
                }
              />
              <StyledOptionText>{option.name}</StyledOptionText>
            </StyledDropdownOption>
          );
        })}
      </StyledOptionContainer>

      {clearable && selectedValues.length > 0 && (
        <StyledClearAllContainer>
          <StyledClearAllButton
            onClick={(e) => {
              e.stopPropagation();
              handleClearAll();
            }}
          >
            Clear All
          </StyledClearAllButton>
        </StyledClearAllContainer>
      )}
    </StyledDropdownContainer>
  );
};

const StyledDropdownContainer = styled.div<{
  $width: string;
  $isOpen: boolean;
}>`
  @media (max-width: 990px) {
    width: 100%;
  }

  position: absolute;
  top: 110%;
  width: ${({ $width }) => $width};
  max-height: ${({ $isOpen }) => ($isOpen ? "328px" : "0")};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.24);
  transition: max-height 0.2s ease-in-out;
  z-index: 100;
`;

const StyledDropdownSearch = styled.div`
  padding: 16px;
  border-bottom: 1px solid #cccccc;
`;

const StyledOptionContainer = styled.div`
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin: 15px 16px;
  outline: none;
`;

const StyledNoOptionMessage = styled.div`
  padding: 16px;
  color: #cccccc;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
`;

const StyledDropdownOption = styled.div<{ selected: boolean }>`
  height: 248px;
  padding: 6px 0px;
  display: flex;
  align-items: center;
  flex: 1 0 0;
  gap: 5px;
  border-radius: 8px;
  background-color: #ffffff;
  transition: background-color 0.3s ease;
  cursor: pointer;

  &:hover {
    background-color: #f2f2f2;
  }
`;

const StyledOptionText = styled.div`
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  white-space: normal;
  overflow: hidden;
  word-wrap: break-word;
`;

const StyledClearAllContainer = styled.div`
  padding: 16px;
  border-top: 1px solid #cccccc;
`;

const StyledClearAllButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: none;
  background-color: #f5f5f5;
  color: #666666;
  cursor: pointer;
  font-size: 14px;
  border-radius: 4px;
  
  &:hover {
    background-color: #eeeeee;
  }
`; 